<template>
    <div class="position-relative">
        <div>
            <h2 class="h4">
                {{ __('customers.contracts.technical_contract_settings') }}
            </h2>
        </div>
        <DListGroup>
            <DListGroupItem class="d-flex justify-content-between align-items-center group-header">
                <div class="div-product">
                    {{ __('customers.contracts.create_contract_tree.product') }}
                </div>
                <div class="div-technical-details">
                    {{ __('customers.contracts.create_contract_tree.technical_details') }}
                </div>
                <div class="div-actions" />
            </DListGroupItem>
            <VueNestable
                v-model="contracts"
                key-prop="key"
                children-prop="children"
            >
                <template #default="{ item }">
                    <DListGroupItem
                        class="d-flex justify-content-between align-items-center group-body"
                    >
                        <div
                            class="div-product"
                        >
                            <div
                                class="product-body"
                                :class="productErrorsExits(item.key) ? 'border-error' : ''"
                            >
                                <div>
                                    {{ quantityFieldValue(item.config_values) }} x
                                </div>
                                <div class="meta">
                                    <div class="product-group-name">
                                        {{ fieldValue(item.product_group_name) }}
                                    </div>
                                    <div class="product-name">
                                        {{ fieldValue(item.product_name) }}
                                    </div>
                                </div>
                                <div>
                                    <span v-if="item.start_date">
                                        {{ item.start_date }}
                                    </span>
                                    <span v-else>
                                        {{ fieldValue(item.start_date) }}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div class="div-technical-details">
                            <div
                                class="technical-details-body"
                                :class="[item.product_id ? '' : 'not-allowed', technicalDetailsErrorsExits(item.key) ? 'border-error' : '']"
                                @click="item.product_id ? setTechnicalDetailsInfo(item) : ''"
                            >
                                <div class="meta">
                                    <div class="domain">
                                        {{ fieldDomainNameValue(item.config_values) }}
                                    </div>
                                    <div class="hostname">
                                        {{ fieldTechnicalDetailsValue(item.config_values) }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </DListGroupItem>
                </template>
            </VueNestable>
        </DListGroup>
        <TechnicalDetailsModal
            v-if="showTechnicalDetailsModal"
            :show-modal="showTechnicalDetailsModal"
            :contract="selectedContract"
            :errors="technicalDetailsErrors"
            @submitTechnicalDetails="setRowForTechnicalDetails"
            @close-modal="showTechnicalDetailsModal = false; selectedContract = null"
        />
    </div>
</template>

<script setup>
import { VueNestable } from '@sparkz-community/vue3-nestable'
import { __ } from '~@/Composables/translation'
import { ref, watch } from 'vue'
import TechnicalDetailsModal from '~@/Components/Customers/Contracts/Modals/ContractTree/TechnicalDetails'

const props = defineProps({
    ffPackageContracts: {
        type: Array,
        default: () => { return [] }
    },
    errors: {
        type: Object,
        default: () => { return {} }
    },
    startDate: {
        type: String,
        default: () => { return '' }
    }
})

const contracts = setContracts()
const showTechnicalDetailsModal = ref(false)
const selectedContract = ref(null)
const technicalDetailsErrors = ref({})

watch(() => props.startDate, (newDate, oldDate) => {
    updateFFPackageContractStartDates(contracts, newDate)
})

const emit = defineEmits(['update:modelValue'])

function setContracts () {
    return props.ffPackageContracts
}

function productErrorsExits (key) {
    return typeof props.errors[`ff_package_contracts.${key}.product_id`] !== 'undefined' ||
        typeof props.errors[`ff_package_contracts.${key}.product_plan_id`] !== 'undefined' ||
        typeof props.errors[`ff_package_contracts.${key}.start_date`] !== 'undefined'
}

function quantityFieldValue (configValues) {
    for (const config of configValues) {
        if (config.name === 'quantity') {
            return config.value
        }
    }

    return 1
}

function fieldValue (value) {
    return value || __('customers.contracts.create_contract_tree.no_data')
}

function technicalDetailsErrorsExits (key) {
    const contract = findNested(contracts, key)

    if (contract !== null) {
        for (const config of contract.config_values) {
            if (typeof props.errors[`ff_package_contracts.${key}.config_values.${config.name}.value`] !== 'undefined') {
                return true
            }
        }
    }

    return false
}

function setTechnicalDetailsInfo (item) {
    getTechnicalDetailsErrors(item.key)
    selectedContract.value = item
    showTechnicalDetailsModal.value = true
}

function getTechnicalDetailsErrors (key) {
    technicalDetailsErrors.value = {}
    const contract = findNested(contracts, key)

    if (contract !== null) {
        for (const config of contract.config_values) {
            if (typeof props.errors[`ff_package_contracts.${key}.config_values.${config.name}.value`] !== 'undefined') {
                technicalDetailsErrors.value[`${config.name}`] = props.errors[`ff_package_contracts.${key}.config_values.${config.name}.value`]
            }
        }
    }
}

function findNested (contracts, key) {
    for (const contract of contracts) {
        if (contract.key === key) {
            return contract
        }

        if (contract.children.length > 0) {
            const match = findNested(contract.children, key)

            if (match !== null) {
                return match
            }
        }
    }

    return null
}

function fieldDomainNameValue (configValues) {
    if (configValues.length === 0) {
        return __('customers.contracts.create_contract_tree.no_data')
    }

    let domainName = null
    let firstConfigName = null

    for (const config of configValues) {
        // Set first config name from array
        if (firstConfigName === null && config.value !== null && config.value !== '') {
            firstConfigName = config.display_value !== null ? config.display_value : config.value
        }

        if (config.name === 'domain' && config.value !== null && config.value !== '') {
            domainName = config.value !== null && config.value !== ''
                ? config.value
                : __('customers.contracts.create_contract_tree.no_data')
        }
    }

    if (domainName !== null) {
        return domainName
    }

    if (firstConfigName !== null) {
        return firstConfigName
    }

    return __('customers.contracts.create_contract_tree.no_data')
}

function fieldTechnicalDetailsValue (configValues) {
    if (configValues.length === 0) {
        return __('customers.contracts.create_contract_tree.no_data')
    }

    let domainNameExist = false
    const configValuesWithoutDomain = []

    for (const config of configValues) {
        if (config.name !== 'domain' && config.value !== null && config.value !== '') {
            configValuesWithoutDomain.push(config.display_value !== null ? config.display_value : config.value)
        }
        if (config.name === 'domain' && config.value !== null && config.value !== '') {
            domainNameExist = true
        }
    }

    if (domainNameExist === true && configValues.length > 0 && configValuesWithoutDomain.length > 0) {
        if (configValuesWithoutDomain.length === 1) {
            return configValuesWithoutDomain[0]
        }

        return configValuesWithoutDomain.join(', ')
    }

    if (domainNameExist === false && configValues.length > 0 && configValuesWithoutDomain.length > 0) {
        // Remove first element from array and then create string comma separated
        const configValuesWithoutDomainWithoutFirstElement = configValuesWithoutDomain.slice(1)
        if (configValuesWithoutDomainWithoutFirstElement.length === 0) {
            return __('customers.contracts.create_contract_tree.no_data')
        }

        if (configValuesWithoutDomainWithoutFirstElement.length > 1) {
            return configValuesWithoutDomainWithoutFirstElement.join(', ')
        }

        return configValuesWithoutDomainWithoutFirstElement[0]
    }

    return __('customers.contracts.create_contract_tree.no_data')
}

function setRowForTechnicalDetails (form) {
    const contract = findNested(contracts, form.key)

    if (contract !== null) {
        contract.config_values = form.config_values
        emit('update:modelValue', contracts)
    }
}

function updateFFPackageContractStartDates (contracts, newStartDate) {
    for (const contract of contracts) {
        contract.start_date = newStartDate

        if (contract.children.length > 0) {
            updateFFPackageContractStartDates(contract.children, newStartDate)
        }
    }
}

</script>

<style lang="scss">
/*
* Style for nestable
*/
.nestable {
    position: relative;
}
.nestable-rtl {
    direction: rtl;
}
.nestable .nestable-list {
    margin: 0;
    padding: 0 0 0 40px;
    list-style-type: none;
}
.nestable-rtl .nestable-list {
    padding: 0 40px 0 0;
}
.nestable > .nestable-list {
    padding: 0;
}
.nestable-item:first-child,
.nestable-item-copy:first-child {
    margin-top: 0;
}
.nestable-item {
    position: relative;
}
.nestable-item.is-dragging .nestable-list {
    pointer-events: none;
}
.nestable-item.is-dragging * {
    opacity: 0;
    filter: alpha(opacity=0);
}
.nestable-item.is-dragging:before {
    content: ' ';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(106, 127, 233, 0.274);
    border: 1px dashed rgb(73, 100, 241);
    -webkit-border-radius: 5px;
    border-radius: 5px;
}
.nestable-drag-layer {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 100;
    pointer-events: none;
}
.nestable-rtl .nestable-drag-layer {
    left: auto;
    right: 0;
}
.nestable-drag-layer > .nestable-list {
    position: absolute;
    top: 0;
    left: 0;
    padding: 0;
    background-color: rgba(106, 127, 233, 0.274);
}
.nestable-rtl .nestable-drag-layer > .nestable-list {
    padding: 0;
}
.nestable-handle {
    display: inline;
}
.group-header {
    border-bottom: 1px solid rgba(0,0,0,0.14);
    align-items: center;

    & > div {
        font-weight: bold;
    }
}
.group-body {
    border-bottom: 1px solid rgba(0,0,0,0.14);
    align-items: center;

    & > div {
        & > div {
            padding: 10px;
            background: var(--colorSecondary);
            border-radius: 5px;
            height: auto;
        }
    }
}
.div-product {
    width: 50%;
    padding-right: 10px;
}
.div-technical-details {
    width: 50%;
    padding-right: 10px;
}
.product-body {
    padding: 15px;
    display: grid;
    grid-template-columns: auto 1fr auto;
    grid-gap: 7px;
    align-items: center;

    & > .meta {
        display: grid;
        grid-gap: 3px;
        padding-right: 5px;
        padding-left: 5px;
        border-left: 1px solid rgba(0,0,0,0.14);
        border-right: 1px solid rgba(0,0,0,0.14);

        & .product-group-name {
            font-weight: normal;
            font-size: 10px;
        }
        & .product-name {
            font-weight: bold;
            word-break: break-all;
            font-size: 11px;
        }
    }
}
.technical-details-body {
    padding: 15px;
    display: grid;
    grid-template-columns: auto;
    align-items: center;
    cursor: pointer;

    & > .meta {
        display: grid;
        grid-gap: 3px;

        & .domain {
            font-weight: bold;
            font-size: 11px;
        }
        & .hostname {
            font-weight: normal;
            font-size: 10px;
        }
    }
}
.not-allowed {
    cursor: not-allowed;
}
.border-error {
    border: 1px solid var(--colorBorderError);
}
</style>
